import { Delete, DriveFileMove, Edit } from '@mui/icons-material'
import {
  Avatar,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import axios from 'axios'
import { Bin } from 'chezy-inventory-backend/bin/database/types/Bin'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import { useEffect, useState } from 'react'

import { getBaseURL } from '../utils/getBaseURL'

import { BinPath } from './binPath'

export const ItemTable = (props: {
  editable?: boolean
  showLocation?: boolean
  items: Item[]
  setItemEditFormOpen?: (item: Item) => void
  setSelectItemMoveFormOpen?: (itemId: string) => void
  perPageDefault?: 10 | 25 | 100
  onRowClick?: (item: Item) => void
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(props.perPageDefault ?? 10)
  const [reloadTS, setReloadTS] = useState(Date.now())
  const [binPaths, setPaths] = useState<Record<string, Bin[]>>({})

  useEffect(() => {
    if (props.showLocation) {
      props.items.forEach((item) => {
        axios.get(`${getBaseURL()}/item/${item.id}/bins`).then(({ data }) => {
          setPaths((m) => ({ ...m, [item.id]: data.bin_path }))
        })
      })
    }
  }, [props.showLocation, props.items])

  useEffect(() => {
    setReloadTS(Date.now())
  }, [props.items])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell key="image" style={{ minWidth: 50 }}>
                Item picture
              </TableCell>
              <TableCell key="id" style={{ minWidth: 50 }}>
                Item ID
              </TableCell>
              <TableCell key="name" style={{ minWidth: 100 }}>
                Item Name
              </TableCell>
              <TableCell key="qty" style={{ minWidth: 100 }}>
                Item Quantity
              </TableCell>
              <TableCell key="location" style={{ minWidth: 50 }}>
                Item Replacement Cost
              </TableCell>
              <TableCell key="instructions" style={{ minWidth: 50 }}>
                Packing instructions
              </TableCell>
              {props.showLocation && (
                <TableCell key="cost" style={{ minWidth: 100 }}>
                  Item Location
                </TableCell>
              )}
              {props.editable && (
                <TableCell key="actions" style={{ minWidth: 100 }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
              return (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row.id}
                  onClick={() => props.onRowClick?.(row)}
                  style={props.onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  <TableCell key="image" style={{ minWidth: 50 }}>
                    <Tooltip
                      title={
                        <img
                          alt={row.name}
                          src={`${getBaseURL()}/item/${row.id}/picture?ts=${reloadTS}`}
                          style={{ maxWidth: 200, maxHeight: 200 }}
                        />
                      }
                      arrow
                    >
                      <Avatar
                        alt={row.name}
                        src={`${getBaseURL()}/item/${row.id}/picture?ts=${reloadTS}`}
                        style={{ height: 50, width: 50 }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell key="id" style={{ minWidth: 50 }}>
                    {row.id}
                  </TableCell>
                  <TableCell key="name" style={{ minWidth: 100 }}>
                    {row.name}
                  </TableCell>
                  <TableCell key="qty" style={{ minWidth: 100 }}>
                    {row.qty}
                  </TableCell>
                  <TableCell key="cost" style={{ minWidth: 50 }}>
                    {row.cost}
                  </TableCell>
                  <TableCell key="instructions" style={{ minWidth: 50 }}>
                    {row.instructions ?? ''}
                  </TableCell>
                  {props.showLocation && (
                    <TableCell key="location" style={{ minWidth: 100 }}>
                      <BinPath binPath={binPaths?.[row.id] ?? []} />
                    </TableCell>
                  )}
                  {props.editable && (
                    <TableCell key="actions" style={{ minWidth: 100 }}>
                      <Tooltip arrow title="Edit item data">
                        <IconButton onClick={() => props.setItemEditFormOpen?.(row)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow title="Move to a different bin">
                        <IconButton onClick={() => props.setSelectItemMoveFormOpen?.(row.id)}>
                          <DriveFileMove />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow title="Delete this item">
                        <IconButton color="error">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={props.items.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}
